import { useCallback, useState } from 'react';
import { NavLink, useLocation } from 'react-router';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import PerpayLogo from '@perpay-web/components/base/PerpayLogo/PerpayLogo';
import HeaderDrawerMenu from '@perpay-web/fintech/components/base/HeaderDrawerMenu/HeaderDrawerMenu';
import { HOME_PAGE_URL } from '@perpay-web/fintech/constants/urls';
import { useCenterHeaderRender } from '@perpay-web/fintech/hooks/useCenterHeaderContext';
import { useHeaderLeftNavRender } from '@perpay-web/fintech/hooks/useHeaderLeftNavContext';
import { useHeaderRightNavRender } from '@perpay-web/fintech/hooks/useHeaderRightNavContext';
import { useTransparentHeaderRender } from '@perpay-web/fintech/hooks/useHeaderTrasparentContext';
import { useShopPath } from '@perpay-web/fintech/hooks/useShopPath';
import { focusFlowPaths, paths } from '@perpay-web/fintech/props/appPaths';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { getClassName } from '@perpay-web/utils/domUtils';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { noop } from '@perpay-web/utils/noop';
import { isAppBrowserAgent } from '@perpay-web/utils/userAgentUtils';

import styles from './DefaultHeader.scss';

const DefaultHeader = ({
    className = '',
    path = '#',
    userData = { fullName: '', accountBalance: undefined },
    isAuthenticated = false,
    isPerpayPlusEligible = false,
    hasPerpayPlus = false,
    onClickPerpayPlus = noop,
    launchSupportToolHandler = noop,
    hasClosedCardAccount = false,
    analyticsHeaderOnClickDashboard = noop,
    analyticsHeaderOnClickShop = noop,
    analyticsHeaderOnClickHamburger = noop,
}) => {
    const [navClicked, setNavClicked] = useState(false);
    // TODO: isMobile is used incorrectly, should be { isMobile } instead
    const isMobile = useIsMobile();
    const isAppBrowser = isAppBrowserAgent();
    const location = useLocation();
    const { lastShopPath } = useShopPath();

    const unauthLogoUrl = isAppBrowser ? paths.signUp.path : HOME_PAGE_URL;
    const defaultHeaderLogoUrl = isAuthenticated
        ? paths.dashboard.path
        : unauthLogoUrl;

    const navClickHandler = () => {
        setNavClicked(!navClicked);
    };

    const renderDefaultRightAuthNav = () => {
        if (!isAuthenticated) {
            return null;
        }

        return (
            <>
                <EndToEndTestIdProvider testId='header-drawer-icon'>
                    <CustomButton
                        action='link'
                        className={styles.profile__block}
                        onClick={() => {
                            navClickHandler();
                            analyticsHeaderOnClickHamburger();
                        }}
                    >
                        <div className={styles.icon__block} />
                    </CustomButton>
                </EndToEndTestIdProvider>
                {navClicked ? (
                    <HeaderDrawerMenu
                        onClose={() => navClickHandler()}
                        fullName={userData.fullName}
                        accountBalance={userData.accountBalance}
                        onClickPerpayPlus={onClickPerpayPlus}
                        launchSupportToolHandler={launchSupportToolHandler}
                        isPerpayPlusEligible={isPerpayPlusEligible}
                        hasClosedCardAccount={hasClosedCardAccount}
                    />
                ) : null}
            </>
        );
    };

    const rightNavRendered = useHeaderRightNavRender();
    const rightNav =
        typeof rightNavRendered === 'undefined'
            ? renderDefaultRightAuthNav()
            : rightNavRendered;

    const leftNavRendered = useHeaderLeftNavRender();

    const renderDefaultLeftNav = useCallback(
        () => (
            <PerpayLogo
                hasPerpayPlus={hasPerpayPlus}
                replace={
                    isAuthenticated
                        ? location.pathname === paths.dashboard.path
                        : false
                }
                href={defaultHeaderLogoUrl}
            />
        ),
        [
            defaultHeaderLogoUrl,
            hasPerpayPlus,
            isAuthenticated,
            location.pathname,
        ],
    );
    const leftNav =
        typeof leftNavRendered === 'undefined'
            ? renderDefaultLeftNav()
            : leftNavRendered;

    const overrideHeaderContainerWidth =
        (!isAuthenticated || path === paths.signUp.path) && !isMobile;

    const renderedCenterHeader = useCenterHeaderRender();
    const renderDefaultCenterHeader = () => {
        if (!isAuthenticated) {
            return null;
        }

        if (
            focusFlowPaths.some((focusFlowPath) =>
                location.pathname.startsWith(focusFlowPath),
            )
        ) {
            return null;
        }

        return (
            <>
                <NavLink
                    onClick={analyticsHeaderOnClickDashboard}
                    to={paths.dashboard.path}
                    className={({ isActive }) =>
                        getClassName(
                            styles.block__text,
                            isActive ? styles['block__text--active'] : null,
                        )
                    }
                >
                    Dashboard
                </NavLink>
                <NavLink
                    onClick={analyticsHeaderOnClickShop}
                    to={lastShopPath}
                    className={({ isActive }) =>
                        getClassName(
                            styles.block__text,
                            isActive ? styles['block__text--active'] : null,
                        )
                    }
                >
                    Shop
                </NavLink>
            </>
        );
    };

    const centerHeader =
        typeof renderedCenterHeader !== 'undefined'
            ? renderedCenterHeader
            : renderDefaultCenterHeader();

    const transparentHeader = useTransparentHeaderRender();

    return (
        <div
            className={getClassName(
                className,
                styles.header,
                transparentHeader ? styles['transparent-header'] : '',
            )}
        >
            <div
                className={getClassName(
                    styles.header__container,
                    overrideHeaderContainerWidth
                        ? styles['unauth--desktop-only']
                        : null,
                )}
            >
                {/* Left Nav */}
                <div className={styles['left-nav__container']}>{leftNav}</div>

                {/* Center Nav */}
                <div className={styles['center-nav__container']}>
                    {centerHeader}
                </div>
                {/* Right Nav */}
                <div className={styles['secondary-navigation__block']}>
                    {rightNav}
                </div>
            </div>
        </div>
    );
};

export default DefaultHeader;
